import styled from 'styled-components'
import React from 'react'
import PropTypes from 'prop-types'
import Dropdown from 'react-select'
import { FormattedMessage } from 'react-intl'
import Tooltip from 'components/Tooltip'
import { TrailingIconsContainer } from 'components/Input/style'
import ErrorIcon from 'assets/icons/error-input-trailing-icon'
import InfoIcon from 'assets/icons/info-icon16px'
import SearchIcon from 'assets/icons/search-icon'
import { SearchSelectIconContainer, SearchSelectIconWrapper, Label, Error, SelectContainer } from './styles'
import {
  createDropdownIndicatorClosed,
  createPlaceholder,
  SingleValueDefault,
  MenuListDefault,
  buildSelectStyles,
  tooltipXOffset,
  tooltipYOffset
} from './shared'

const SelectTrailingIconsContainer = styled(TrailingIconsContainer)`
  right: 32px;

  img {
    margin-right: 0;
  }
`

const Select = ({
  value,
  onChange,
  options,
  label,
  field,
  error,
  errorMarginTop = "16px",
  placeholder,
  height = '48px',
  menuPlacement,
  marginTop = '8px',
  marginBottom = '32px',
  indicatorSize,
  dataTest,
  disabled,
  placeholderComponent: PlaceholderComponent,
  closeMenuOnSelect = true,
  singleValueComponent: SingleValueComponent,
  menuListComponent: MenuListComponent,
  infoTooltip,
  zIndex,
  isSearchable = false,
  paddingLeft = `${isSearchable ? '30px' : '8px'}`,
  menuWidth = '100%',
  menuIsOpen,
  onFocus,
  onBlur,
  inputValue,
  onInputChange,
  isOptional = false,
  link,
  twoStepWidth,
  hyper,
  searchDropdown = false,
  marginBottomLabel,
  inputMargin = '12px 0 12px 8px',
  filterOption,
  title
}) => {
  const customStyles = buildSelectStyles({
    error,
    field,
    height,
    menuWidth,
    inputMargin,
    paddingLeft,
    searchDropdown,
    twoStepWidth,
    includeExpandedMenu: true,
    allowDisabledPointerStyle: false
  })

  const DropdownIndicator = createDropdownIndicatorClosed(indicatorSize)
  const Placeholder = PlaceholderComponent || createPlaceholder(placeholder)
  const SingleValue = SingleValueComponent || SingleValueDefault
  const MenuList = MenuListComponent || MenuListDefault

  const onBlurHandler = () => {
    if (onBlur) {
      onBlur()
    }
  }

  const toggleDropdown = () => {
    if (disabled) {
      return
    }
  }

  return (
    <SelectContainer data-testid='test-select-container' marginBottom={marginBottom} onClick={toggleDropdown}>
      {React.isValidElement(label)
        ? label
        : label && (
          <>
            <Label htmlFor={`dropdown-${field}`} data-test={`label-${dataTest}`} marginBottomLabel={marginBottomLabel}>
              <section style={{ display: 'flex', alignItems: 'center' }}>
                <FormattedMessage id={label} />
                {infoTooltip && (
                  <Tooltip
                    style={{ zIndex: 1000 }}
                    zIndex={zIndex}
                    placement='top'
                    trigger={['hover']}
                    align={{ offset: [tooltipXOffset, tooltipYOffset] }}
                    overlay={{ label: label, infoTooltip: infoTooltip }}
                    link={link}
                    hyper={hyper}
                  >
                    <div>
                      <InfoIcon />
                    </div>
                  </Tooltip>
                )}
              </section>
              <span>{isOptional && <FormattedMessage id='optional' />}</span>
            </Label>
            {isSearchable && (
              <SearchSelectIconContainer>
                <SearchSelectIconWrapper>
                  <SearchIcon />
                </SearchSelectIconWrapper>
              </SearchSelectIconContainer>
            )}
          </>
        )}
      <div style={{ position: 'relative' }}>
        <Dropdown
          components={{ DropdownIndicator, Placeholder, SingleValue, MenuList }}
          id={`dropdown-${field}`}
          value={value}
          onChange={onChange}
          styles={customStyles}
          options={options}
          menuPlacement={menuPlacement}
          data-test={dataTest}
          isSearchable={isSearchable}
          isDisabled={disabled}
          closeMenuOnSelect={closeMenuOnSelect}
          menuIsOpen={menuIsOpen}
          onFocus={onFocus}
          onBlur={onBlurHandler}
          inputValue={inputValue}
          onInputChange={onInputChange}
          filterOption={filterOption}
          title={title}
        />
        <SelectTrailingIconsContainer>
          {error && error.field === field && (
            <ErrorIcon />
          )}
        </SelectTrailingIconsContainer>
      </div>
      {error && error.field === field && (
        <Error errorMarginTop={errorMarginTop} data-test='validation-error-message'>
          <FormattedMessage id={error.error} />
        </Error>
      )}
    </SelectContainer>
  )
}

export default Select

Select.propTypes = {
  value: PropTypes.any,
  onChange: PropTypes.func,
  options: PropTypes.arrayOf(PropTypes.any),
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  field: PropTypes.string,
  error: PropTypes.shape({
    field: PropTypes.string,
    error: PropTypes.string
  }),
  errorMarginTop: PropTypes.string,
  placeholder: PropTypes.string,
  height: PropTypes.string,
  menuPlacement: PropTypes.string,
  marginTop: PropTypes.string,
  marginBottom: PropTypes.string,
  indicatorSize: PropTypes.string,
  dataTest: PropTypes.string,
  disabled: PropTypes.bool,
  placeholderComponent: PropTypes.elementType,
  closeMenuOnSelect: PropTypes.bool,
  singleValueComponent: PropTypes.elementType,
  menuListComponent: PropTypes.elementType,
  infoTooltip: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  zIndex: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isSearchable: PropTypes.bool,
  paddingLeft: PropTypes.string,
  menuWidth: PropTypes.string,
  menuIsOpen: PropTypes.bool,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  inputValue: PropTypes.string,
  onInputChange: PropTypes.func,
  isOptional: PropTypes.bool,
  link: PropTypes.string,
  twoStepWidth: PropTypes.bool,
  hyper: PropTypes.string,
  searchDropdown: PropTypes.bool,
  marginBottomLabel: PropTypes.string,
  inputMargin: PropTypes.string,
  filterOption: PropTypes.func,
  title: PropTypes.string
}
